import { parsePatchFiles } from "@pierre/diffs"
import { RefreshCw } from "lucide-react"
import { observer } from "mobx-react"
import { memo, useEffect, useLayoutEffect, useMemo, useState } from "react"
import type { ChangedFileInfo, GetDifftasticResponse, GetFilePairResponse, GetFilePatchResponse } from "../nestAPIs/git"
import { gitApi } from "../nestAPIs/git"
import { useCodeStore } from "../store/context"
import type { ChangesManager } from "../store/managers/ChangesManager"
import { getPatchContextLines, type PatchContextLines } from "../utils/gitDiffContext"
import type { GitDiffStrategy } from "../utils/gitDiffStrategy"
import { type AnnotationSide, type CommentHandlers, FileDiffViewer } from "./FilesAndDiffs"
import { FilePathActions } from "./FilePathActions"
import { GitBinaryFilePreview } from "./GitBinaryFilePreview"
import { DiffStrategyView } from "./diffLab/DiffStrategyView"
import { StatusIcon } from "./git/shared"
import { TestFileFilter } from "./git/TestFileFilter"
import { buildFileTree, collectFileTreeFiles } from "./utils/changesTree"
import { joinPath } from "./utils/paths"
import { hasImageExtension, hasPdfExtension } from "./utils/pathReference"

const EMPTY_TREE = "4b825dc642cb6eb9a060e54bf8d69288fbee4904"
export type GitPatchSource = { kind: "worktree"; fromTreeish: string } | { kind: "commit"; commit: string; parentCount: number }

interface SharedGitReviewProps {
    taskId: string
    workDir: string
    workspaceId: string
    workspaceRoot: string
    worktreeId?: string
    className?: string
}

interface CompactGitReviewProps extends SharedGitReviewProps {
    changesManager: ChangesManager
}

interface SelectedFileGitReviewProps extends SharedGitReviewProps {
    file: ChangedFileInfo
    patchSource: GitPatchSource
    contextLines: PatchContextLines
    diffStyle?: "split" | "unified"
    diffStrategy?: GitDiffStrategy
    showPathActions?: boolean
    stickyHeaderOffset?: number
}

interface GitFileDiffProps extends SharedGitReviewProps {
    file: ChangedFileInfo
    patchSource: GitPatchSource
    contextLines: PatchContextLines
    diffStyle: "split" | "unified"
    diffStrategy: GitDiffStrategy
    commentable: boolean
    showPathActions: boolean
    stickyHeaderOffset: number
}

function patchSourceKey(source: GitPatchSource): string {
    return source.kind === "worktree" ? `worktree:${source.fromTreeish}` : `commit:${source.commit}`
}

const GitFileDiff = memo(function GitFileDiff({
    file,
    patchSource,
    contextLines,
    diffStyle,
    diffStrategy,
    commentable,
    showPathActions,
    stickyHeaderOffset,
    taskId,
    workDir,
    workspaceId,
    workspaceRoot,
    worktreeId,
}: GitFileDiffProps): JSX.Element {
    const [patch, setPatch] = useState<GetFilePatchResponse | null>(null)
    const [pair, setPair] = useState<GetFilePairResponse | null>(null)
    const [difftastic, setDifftastic] = useState<GetDifftasticResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [difftasticLoading, setDifftasticLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [renderHeavy, setRenderHeavy] = useState(false)
    const sourceKey = patchSourceKey(patchSource)
    const loadKey = `${sourceKey}:${file.path}:${file.oldPath ?? ""}:U${contextLines}:${diffStrategy}`
    const previewPath = file.status === "deleted" ? (file.oldPath ?? file.path) : file.path
    const supportsBinaryPreview = hasImageExtension(previewPath) || hasPdfExtension(previewPath)
    const previewTreeish =
        patchSource.kind === "worktree"
            ? file.status === "deleted"
                ? patchSource.fromTreeish
                : undefined
            : file.status === "deleted" && patchSource.parentCount > 0
              ? `${patchSource.commit}^`
              : patchSource.commit

    useEffect(() => {
        setPatch(null)
        setPair(null)
        setDifftastic(null)
        setError(null)
        setLoading(false)
        setDifftasticLoading(false)
        setRenderHeavy(false)
    }, [loadKey])

    useEffect(() => {
        if (file.binary || supportsBinaryPreview || error) return
        if (diffStrategy === "current" && patch) return
        if (diffStrategy !== "current" && pair) return
        let requestActive = true
        const controller = new AbortController()
        setLoading(true)
        void (async () => {
            if (!requestActive) throw new DOMException("Patch load cancelled", "AbortError")
            if (diffStrategy === "current") {
                if (patchSource.kind === "worktree") {
                    const value = await gitApi.getWorktreeFilePatch(
                        {
                            workspaceId,
                            workspaceRoot,
                            directory: workDir,
                            ...(worktreeId ? { worktreeId } : {}),
                            fromTreeish: patchSource.fromTreeish,
                            filePath: file.path,
                            oldPath: file.oldPath,
                            contextLines,
                        },
                        { signal: controller.signal }
                    )
                    return { kind: "patch" as const, value }
                }
                const value = await gitApi.getCommitFilePatch(
                    {
                        workspaceId,
                        workspaceRoot,
                        directory: workDir,
                        ...(worktreeId ? { worktreeId } : {}),
                        commit: patchSource.commit,
                        filePath: file.path,
                        oldPath: file.oldPath,
                        contextLines,
                    },
                    { signal: controller.signal }
                )
                return { kind: "patch" as const, value }
            }
            if (patchSource.kind === "commit" && patchSource.parentCount === 0) {
                const after = await gitApi.getFileAtTreeish(
                    {
                        workspaceId,
                        workspaceRoot,
                        directory: workDir,
                        ...(worktreeId ? { worktreeId } : {}),
                        treeish: patchSource.commit,
                        filePath: file.path,
                    },
                    { signal: controller.signal }
                )
                return { kind: "pair" as const, value: { before: "", after: after.content, tooLarge: after.tooLarge } }
            }
            const value = await gitApi.getFilePair(
                {
                    workspaceId,
                    workspaceRoot,
                    directory: workDir,
                    ...(worktreeId ? { worktreeId } : {}),
                    fromTreeish: patchSource.kind === "worktree" ? patchSource.fromTreeish : `${patchSource.commit}^`,
                    toTreeish: patchSource.kind === "worktree" ? "" : patchSource.commit,
                    filePath: file.path,
                    oldPath: file.oldPath,
                },
                { signal: controller.signal }
            )
            return { kind: "pair" as const, value }
        })()
            .then((result) => {
                if (!requestActive) return
                if (result.kind === "patch") {
                    setPatch(result.value)
                } else {
                    setPair(result.value)
                }
            })
            .catch((cause: unknown) => {
                if (!requestActive) return
                setError(cause instanceof Error ? cause.message : "Diff unavailable")
            })
            .finally(() => {
                if (requestActive) setLoading(false)
            })
        return () => {
            requestActive = false
            controller.abort()
        }
    }, [contextLines, diffStrategy, error, file, pair, patch, patchSource, supportsBinaryPreview, workDir, workspaceId, workspaceRoot, worktreeId])

    useEffect(() => {
        if (diffStrategy !== "difftastic" || file.binary || supportsBinaryPreview || difftastic || error) return
        let requestActive = true
        const controller = new AbortController()
        setDifftasticLoading(true)
        void gitApi
            .getDifftastic(
                {
                    workspaceId,
                    workspaceRoot,
                    directory: workDir,
                    ...(worktreeId ? { worktreeId } : {}),
                    fromTreeish:
                        patchSource.kind === "worktree" ? patchSource.fromTreeish : patchSource.parentCount === 0 ? EMPTY_TREE : `${patchSource.commit}^`,
                    toTreeish: patchSource.kind === "worktree" ? "" : patchSource.commit,
                    filePath: file.path,
                    oldPath: file.oldPath,
                    contextLines,
                },
                { signal: controller.signal }
            )
            .then((result) => {
                if (requestActive) setDifftastic(result)
            })
            .catch((cause: unknown) => {
                if (!requestActive) return
                setDifftastic({
                    available: true,
                    output: "",
                    error: cause instanceof Error ? cause.message : "Structural preview failed.",
                    elapsedMs: 0,
                    truncated: false,
                    hitTimeout: false,
                })
            })
            .finally(() => {
                if (requestActive) setDifftasticLoading(false)
            })
        return () => {
            requestActive = false
            controller.abort()
        }
    }, [contextLines, diffStrategy, difftastic, error, file, patchSource, supportsBinaryPreview, workDir, workspaceId, workspaceRoot, worktreeId])

    const patchView = useMemo(() => {
        if (!patch?.patch || (patch.heavy && !renderHeavy)) return { fileDiff: null, parseError: false }
        try {
            for (const parsed of parsePatchFiles(patch.patch, file.path)) {
                if (parsed.files[0]) return { fileDiff: parsed.files[0], parseError: false }
            }
            return { fileDiff: null, parseError: false }
        } catch (cause) {
            console.error("[SelectedFileGitReview] Failed to parse patch:", cause)
            return { fileDiff: null, parseError: true }
        }
    }, [file.path, patch, renderHeavy])

    const commentHandlers = useMemo<CommentHandlers | null>(() => {
        if (!commentable) return null
        return {
            taskId,
            sourceMatch: (comment) => comment.source.type === "file" && comment.source.filePath === file.path,
            createSource: (lineStart: number, lineEnd: number, _side: AnnotationSide) => ({
                type: "file",
                filePath: file.path,
                lineStart,
                lineEnd,
            }),
        }
    }, [commentable, file.path, taskId])

    const absolutePath = showPathActions ? joinPath(workDir, file.path) : null

    return (
        <section
            className="border-y border-border bg-base-100 first:border-t-0"
            style={{ scrollMarginTop: stickyHeaderOffset }}
            data-selected-git-file={file.path}
        >
            <div
                className="sticky z-[1] flex min-h-11 items-center gap-2 border-b border-border bg-base-200/95 px-3 backdrop-blur-sm"
                style={{ top: stickyHeaderOffset }}
            >
                <StatusIcon status={file.status} />
                <span className="min-w-0 flex-1 break-all font-mono text-[11px] leading-4">{file.path}</span>
                {patch && (
                    <span className="shrink-0 font-mono text-[10px] tabular-nums">
                        {patch.stats.insertions > 0 && <span className="text-success">+{patch.stats.insertions}</span>}
                        {patch.stats.insertions > 0 && patch.stats.deletions > 0 && " "}
                        {patch.stats.deletions > 0 && <span className="text-error">−{patch.stats.deletions}</span>}
                    </span>
                )}
                {absolutePath && <FilePathActions path={absolutePath} workingDir={workDir} />}
            </div>
            <div data-selected-git-file-body>
                {file.binary || supportsBinaryPreview ? (
                    <GitBinaryFilePreview
                        workspaceId={workspaceId}
                        workspaceRoot={workspaceRoot}
                        worktreeId={worktreeId}
                        workDir={workDir}
                        filePath={previewPath}
                        treeish={previewTreeish}
                    />
                ) : loading || (diffStrategy === "difftastic" && difftasticLoading) ? (
                    <div className="flex min-h-28 items-center justify-center text-xs text-muted">Loading diff…</div>
                ) : error ? (
                    <div className="px-4 py-8 text-center text-xs text-error">{error}</div>
                ) : diffStrategy !== "current" ? (
                    <DiffStrategyView
                        strategy={diffStrategy}
                        file={file}
                        pair={pair}
                        patch={null}
                        loading={false}
                        error={null}
                        diffStyle={diffStyle}
                        difftastic={difftastic}
                        difftasticLoading={false}
                        pairElapsedMs={null}
                        patchElapsedMs={null}
                        difftasticRoundTripMs={null}
                        commentHandlers={commentHandlers}
                        showTimings={false}
                    />
                ) : patch?.heavy && !renderHeavy ? (
                    <div className="flex min-h-32 flex-col items-center justify-center gap-2 px-4 py-6 text-center text-xs text-muted">
                        <span>{patch.stats.changedLines} changed lines. Preview deferred for this large diff.</span>
                        <button
                            type="button"
                            className="btn border border-border bg-base-200 px-3 py-1.5 text-base-content"
                            onClick={() => setRenderHeavy(true)}
                        >
                            Show diff
                        </button>
                    </div>
                ) : patchView.parseError ? (
                    <div className="px-4 py-8 text-center text-xs text-muted">Diff preview unavailable.</div>
                ) : patchView.fileDiff ? (
                    <div className="overflow-x-auto bg-editor-background">
                        {patch?.truncated && (
                            <div className="border-b border-border bg-base-200 px-3 py-2 text-xs text-muted">Large diff preview truncated.</div>
                        )}
                        <FileDiffViewer
                            fileDiff={patchView.fileDiff}
                            diffStyle={diffStyle}
                            disableFileHeader
                            disableWorkerPool={patch?.heavy}
                            commentHandlers={commentHandlers}
                            options={{
                                ...(patch?.heavy ? { lineDiffType: "none" as const, maxLineDiffLength: 0, tokenizeMaxLineLength: 0 } : {}),
                                overflow: "scroll",
                            }}
                        />
                    </div>
                ) : patch ? (
                    <div className="px-4 py-8 text-center text-xs text-muted">No textual diff.</div>
                ) : (
                    <div className="min-h-28" />
                )}
            </div>
        </section>
    )
})

export const SelectedFileGitReview = observer(function SelectedFileGitReview({
    file,
    patchSource,
    contextLines,
    diffStyle = "unified",
    diffStrategy = "current",
    showPathActions = false,
    stickyHeaderOffset = 0,
    taskId,
    workDir,
    workspaceId,
    workspaceRoot,
    worktreeId,
    className,
}: SelectedFileGitReviewProps): JSX.Element {
    const sourceKey = patchSourceKey(patchSource)

    return (
        <div className={`min-h-0 overflow-y-auto overscroll-contain bg-base-200 ${className ?? ""}`} data-selected-git-review="true">
            <div className="py-3">
                <GitFileDiff
                    key={`${sourceKey}:${file.oldPath ?? ""}:${file.path}`}
                    file={file}
                    patchSource={patchSource}
                    contextLines={contextLines}
                    diffStyle={diffStyle}
                    diffStrategy={diffStrategy}
                    commentable={patchSource.kind === "worktree"}
                    showPathActions={showPathActions}
                    stickyHeaderOffset={stickyHeaderOffset}
                    taskId={taskId}
                    workDir={workDir}
                    workspaceId={workspaceId}
                    workspaceRoot={workspaceRoot}
                    worktreeId={worktreeId}
                />
            </div>
        </div>
    )
})

export const CompactGitReview = observer(function CompactGitReview({
    changesManager,
    taskId,
    workDir,
    workspaceId,
    workspaceRoot,
    worktreeId,
    className,
}: CompactGitReviewProps): JSX.Element {
    const codeStore = useCodeStore()
    const sourceFiles = changesManager.files
    const files = useMemo(() => collectFileTreeFiles(buildFileTree(sourceFiles)), [sourceFiles])
    const contextLines = getPatchContextLines(codeStore.ui.diffContext)
    const selectedPath = changesManager.selectedFilePath
    const activeFile = files.find((file) => file.path === selectedPath) ?? files[0] ?? null
    const patchSource = useMemo<GitPatchSource>(() => ({ kind: "worktree", fromTreeish: changesManager.fromTreeish }), [changesManager.fromTreeish])

    useLayoutEffect(() => {
        if (changesManager.canCompareFromBase && changesManager.diffSource !== "from-base") {
            changesManager.setDiffSource("from-base")
        }
    }, [changesManager, changesManager.canCompareFromBase, changesManager.diffSource])

    const sourceLabel = changesManager.diffSource === "from-base" ? "Changes from base" : "Uncommitted changes"
    return (
        <div className={`flex h-full min-w-0 flex-col bg-base-100 ${className ?? ""}`} data-compact-git-review="true">
            <div className="sticky top-0 z-[2] flex min-h-11 items-center gap-2 border-b border-border bg-base-100/95 px-3 backdrop-blur-sm">
                <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-base-content">{sourceLabel}</div>
                    <div className="text-[10px] tabular-nums text-muted">
                        {changesManager.isLoading ? "Loading…" : `${files.length} file${files.length === 1 ? "" : "s"}`}
                    </div>
                </div>
                <TestFileFilter settings={changesManager} testFileCount={changesManager.testFileCount} iconOnly />
                <button
                    type="button"
                    className="btn flex h-9 w-9 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                    onClick={() => changesManager.refresh()}
                    aria-label="Refresh changes"
                >
                    <RefreshCw size={15} />
                </button>
            </div>

            {changesManager.isLoading ? (
                <div className="py-16 text-center text-sm text-muted">Loading changes…</div>
            ) : files.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted">
                    {!changesManager.showTestFiles && changesManager.testFileCount > 0
                        ? `All ${changesManager.testFileCount} changed test ${changesManager.testFileCount === 1 ? "file is" : "files are"} excluded.`
                        : "Clean"}
                </div>
            ) : (
                <div className="flex min-h-0 flex-1 flex-col">
                    <nav className="max-h-[36vh] shrink-0 overflow-y-auto border-b border-border bg-base-100" aria-label="Changed files">
                        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">Files changed</div>
                        {files.map((file) => (
                            <button
                                key={`${file.oldPath ?? ""}:${file.path}`}
                                type="button"
                                className={`btn flex min-h-10 w-full items-center gap-2 border-t border-border/60 px-3 py-2 text-left hover:bg-base-200 ${
                                    activeFile?.path === file.path ? "bg-primary/10 text-base-content" : "text-muted"
                                }`}
                                onClick={() => changesManager.selectFile(file.path)}
                                aria-current={activeFile?.path === file.path ? "true" : undefined}
                                title={file.path}
                            >
                                <StatusIcon status={file.status} />
                                <span className="min-w-0 flex-1 break-all font-mono text-[11px] leading-4">{file.path}</span>
                            </button>
                        ))}
                    </nav>
                    {activeFile && (
                        <SelectedFileGitReview
                            file={activeFile}
                            patchSource={patchSource}
                            contextLines={contextLines}
                            stickyHeaderOffset={0}
                            className="flex-1"
                            taskId={taskId}
                            workDir={workDir}
                            workspaceId={workspaceId}
                            workspaceRoot={workspaceRoot}
                            worktreeId={worktreeId}
                        />
                    )}
                </div>
            )}
        </div>
    )
})
